import { useMemo, useState } from 'react';
import type { Conn } from '../conn';
import type { Provider } from '../api';

interface Entry { provider: Provider; label: string; model: string; routed: boolean; tags: string }
const CATALOG: Entry[] = [
  { provider: 'anthropic', label: 'Anthropic', model: 'claude-opus-4', routed: true, tags: 'reasoning flagship' },
  { provider: 'anthropic', label: 'Anthropic', model: 'claude-sonnet-4-5', routed: true, tags: 'balanced default' },
  { provider: 'anthropic', label: 'Anthropic', model: 'claude-haiku-4-5', routed: true, tags: 'fast cheap' },
  { provider: 'openrouter', label: 'OpenRouter', model: 'anthropic/claude-sonnet-4.5', routed: true, tags: 'balanced' },
  { provider: 'openrouter', label: 'OpenAI', model: 'openai/gpt-4o', routed: false, tags: 'multimodal' },
  { provider: 'openrouter', label: 'OpenAI', model: 'openai/gpt-4o-mini', routed: false, tags: 'fast cheap' },
  { provider: 'openrouter', label: 'Gemini', model: 'google/gemini-2.5-pro', routed: false, tags: 'long-context' },
  { provider: 'openrouter', label: 'Gemini', model: 'google/gemini-2.5-flash', routed: false, tags: 'fast cheap' },
  { provider: 'openrouter', label: 'Grok', model: 'x-ai/grok-4', routed: false, tags: 'reasoning' },
  { provider: 'openrouter', label: 'Grok', model: 'x-ai/grok-4-mini', routed: false, tags: 'fast' },
];

export default function ModelsPage({ conn, onChange }: { conn: Conn; onChange: (c: Partial<Conn>) => void }) {
  const [q, setQ] = useState('');
  const [pf, setPf] = useState<'all' | 'anthropic' | 'openai' | 'gemini' | 'grok'>('all');

  const filtered = useMemo(() => CATALOG.filter((e) => {
    const matchQ = (e.model + e.label + e.tags).toLowerCase().includes(q.toLowerCase());
    const matchP = pf === 'all' || e.label.toLowerCase() === pf;
    return matchQ && matchP;
  }), [q, pf]);

  const isCurrent = (e: Entry) =>
    (e.provider === 'anthropic' && conn.provider === 'anthropic' && (conn.model === e.model || (!conn.model && e.model === 'claude-sonnet-4-5')))
    || (e.provider === 'openrouter' && conn.provider === 'openrouter' && conn.model === e.model);

  return (
    <div className="page">
      <header className="page-head"><h1>Models</h1></header>
      <p className="muted small">Browse models across providers and set the default used for runs &amp; Create-agent. Direct providers route natively; others reach via OpenRouter today. Saved in this browser.</p>

      <div className="cards">
        <div className="card" style={{ flex: 2 }}>
          <div className="card-k">Default provider</div>
          <select value={conn.provider} onChange={(e) => onChange({ provider: e.target.value as Provider })}>
            <option value="anthropic">anthropic</option>
            <option value="openrouter">openrouter</option>
          </select>
        </div>
        <div className="card" style={{ flex: 3 }}>
          <div className="card-k">Default model (blank = provider default)</div>
          <input value={conn.model} placeholder="e.g. google/gemini-2.5-flash" onChange={(e) => onChange({ model: e.target.value })} />
        </div>
      </div>

      <div className="toolbar">
        <div className="tabs">
          {(['all', 'anthropic', 'openai', 'gemini', 'grok'] as const).map((p) => (
            <button key={p} className={`tab ${pf === p ? 'active' : ''}`} onClick={() => setPf(p)}>{p}</button>
          ))}
        </div>
        <div className="search-wrap"><input placeholder="Search models…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <span className="count-pill">{filtered.length} models</span>
      </div>

      {filtered.length === 0
        ? <div className="empty">No models match.</div>
        : <div className="browse-grid">
            {filtered.map((e) => (
              <div key={e.provider + e.model} className={`browse-card ${isCurrent(e) ? 'active' : ''}`}>
                <div className="bc-title"><span className="ico">⚙</span>{e.label}</div>
                <div className="bc-desc"><code>{e.model}</code></div>
                <div className="bc-meta">
                  {e.routed ? <span className="badge green">native</span> : <span className="badge">via OpenRouter</span>}
                  {e.tags.split(' ').map((t) => <span key={t} className="badge">{t}</span>)}
                </div>
                <div className="bc-actions">
                  {isCurrent(e)
                    ? <span className="badge accent">● current default</span>
                    : <button className="link grow" onClick={() => onChange({ provider: e.provider, model: e.provider === 'anthropic' && e.model === 'claude-sonnet-4-5' ? '' : e.model })}>Set as default</button>}
                </div>
              </div>
            ))}
          </div>}
      <p className="muted small">Native OpenAI/Gemini/Grok routing + server-side defaults arrive with the provider-router + layered-settings work (design §8, §10).</p>
    </div>
  );
}
